<script setup>
import { computed, inject, provide, ref } from 'vue'
import Sprite from '@/__core__/components/sprite/Sprite.vue'
import ResultHeader from '@/__v1.5__/components/result-headers/ResultHeader.vue'
import useFetch from '@/__core__/composables/useFetch'
import { useLiveScoreboardStore } from '@/widgets/livescore/LiveScoreboard.store'
import PopupContainer from '@/__v1.5__/components/popups/PopupContainer.vue'
import { loc } from '@/__core__/localization/localization'
// import PreviousMeetings from '@/__v1.5__/widgets/teamProfile/PreviousMeetings.vue'
import PeriodOverview from '@/__core__/components/period-overview/PeriodOverview.vue'

const props = defineProps({
  sportType: String,
  liveScoreboardProps: Array,
  event: {
    type: Object,
    default: () => null
  }
})

const langId = inject('langId', '')

const emit = defineEmits(['popup-close'])
const liveScoreboardStore = useLiveScoreboardStore()
const widgetWidth = inject('width', ref(0))

const responses = ref([])
const resultHeaderPropsArray = ref([])
const currentIndex = ref(0)

const resultHeaderWrapperWidth = computed(() =>
  widgetWidth.value > 360 ? 360 : widgetWidth.value - 20
)

const isLoading = computed(() => status.value === 'loading')

const labels = computed(() =>
  props.liveScoreboardProps.map((p, index) => ({
    id: p.eventId.toString(),
    name: `${loc('sm_knockout_game', langId)} - ${index + 1}`
  }))
)

// Initialize with placeholder headers using props.event scores
function initializeScorers() {

  const [homeScore, awayScore] = props.event?.teams?.map(t => t.score) || [0, 0]
  const maxScore = Math.max(homeScore, awayScore)
  const scorers = Array.from({ length: maxScore }).map((_, index) => ({
    id: index + 1,
    home: index < homeScore,
    away: index < awayScore,
  }))

  resultHeaderPropsArray.value = props.liveScoreboardProps.map(() => ({
    width: resultHeaderWrapperWidth.value,
    header: null,
    hasShadow: false,
    hasCompetition: false,
    hasScorersSkeleton: true,
    ...(props.sportType !== 'basketball' && { scorers }),
    venue: null,
    previousMeetings: null,
    periodScores: [],
    upcoming: false
  }))

}

// Initialize immediately so we have skeletons on mount
initializeScorers()

// Match active index based on props.event
function getInitialIndex() {
  const found = props.liveScoreboardProps.findIndex(p => p.eventId === props.event?.eventId)
  return found !== -1 ? found : 0
}

currentIndex.value = getInitialIndex()

const { status } = useFetch(async () => {
  const results = await Promise.all(
    props.liveScoreboardProps.map(p => liveScoreboardStore.fetchLiveScoreboardData(p))
  )

  responses.value = results

  resultHeaderPropsArray.value = results.map(res => ({
    width: resultHeaderWrapperWidth.value,
    header: { ...res.header, competitionLabel: '' },
    hasShadow: false,
    hasCompetition: false,
    hasScorersSkeleton: true,
    scorers: res.scorers,
    previousMeetings: res.previousMeetings,
    periodScores: res.periodScores,
    upcoming: res.header?.upcoming,
    venue: res.venue,
  }))
})

function closePopup() {
  if (!isLoading.value) emit('popup-close')
}

function prev() {
  if (currentIndex.value > 0) currentIndex.value--
}

function next() {
  if (currentIndex.value < labels.value.length - 1) currentIndex.value++
}

const preparePeriodLabels = (periodScores) => (
  periodScores.map(per => {
    let label = ''
    const number = per.number ? per.number : ''

    if (per.type === 'regular_period') {
      label = `${number}Q`
    } else {
      label = `OT${number}`
    }

    return {
      ...per,
      periodLabel: label
    }
  })
)

provide('status', status)
</script>

<template>
  <popup-container
    content-classes="szw-relative szw-max-w-[360px] szw-p-2.5 szw-w-full"
    @popup-close="closePopup"
  >
    <div class="szw-h-fit szw-w-full szw-rounded-2 szw-bg-background szw-p-2.5">
      <!-- Top Row -->
      <div class="szw-h-8 szw-flex szw-flex-row szw-justify-between szw-items-center szw-mb-2"> 
        <div class="szw-ml-2">{{ loc('sm_knockout_match_details', langId) }}</div>  
        <button
          v-if="!isLoading"
          @click="closePopup"
          class="szw-h-8 szw-w-8 szw-flex szw-justify-center szw-items-center szw-bg-transparent szw-ml-auto"
          e2e-id="event-popup-close"
        >
          <sprite sprite-id="latest-close" />
        </button>
      </div>

      <!-- Navigation Arrows -->
      <div class="szw-flex szw-items-center szw-justify-between" v-if="labels.length > 1">
        <div class="szw-z-50 szw-cursor-pointer szw-w-[32px] szw-h-[32px] szw-justify-center szw-items-center szw-flex" @click="prev" :class="{ 'szw-opacity-30': currentIndex === 0 }">
          <sprite sprite-id="arrow-back" class="szw-text-latest-color-primary"/>
        </div>

        <div class="szw-min-w-[100px] szw-text-center szw-text-base szw-font-medium">
          {{ labels[currentIndex]?.name }}
        </div>

        <div class="szw-z-50 szw-180-rotate szw-cursor-pointer szw-w-[32px] szw-h-[32px] szw-justify-center szw-items-center szw-flex" @click="next" :class="{ 'szw-opacity-30': currentIndex === labels.length - 1 }">
          <sprite sprite-id="arrow-back" class="szw-text-latest-color-primary"/>
        </div>
      </div>

      <!-- Slide Result Header -->
      <div class="szw-w-full szw-overflow-hidden szw-relative">
        <div
          class="szw-flex szw-transition-transform szw-duration-300 szw-ease-in-out"
          :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
        >
          <div
            v-for="(header, i) in resultHeaderPropsArray"
            :key="i"
            class="szw-w-full szw-flex-shrink-0 szw-flex szw-flex-col"
          >
            <result-header v-bind="header"/>
            
            <period-overview v-if="sportType === 'basketball' && header.periodScores?.length" :home-team="header?.header?.home" :away-team="header?.header?.away" :periods="preparePeriodLabels(header.periodScores)" :key="`per${i}`"/>
            <!-- <PreviousMeetings
              v-if="header.upcoming"
              class="szw-mt-auto"
              :home-wins="header.previousMeetings?.wins"
              :away-wins="header.previousMeetings?.losses"
              :draws="header.previousMeetings?.draws"
              :label="loc('sm_previous_matches', langId)"
            /> -->
            <div v-if="header.upcoming" class="szw-gap-1 szw-p-1 szw-text-[12px] szw-leading-[14px] szw-tracking-[0%] szw-flex szw-justify-between szw-w-full szw-items-center">
              <template v-if="status === 'loading'">
                <div class="szw-w-[80px] szw-bg-section-static szw-h-[14px] szw-rounded-1 szw-animate-pulse"></div>
                <div class="szw-w-[100px] szw-bg-section-static szw-h-[14px] szw-rounded-1 szw-animate-pulse"></div>
              </template>

              <template v-if="status === 'success'">
                <span class="szw-font-light">{{ loc('sm_stadium', langId) }}</span>
                <span class="szw-font-medium">{{ header.venue?.name || '-' }}</span>
              </template>
              </div>
          </div>
        </div>
      </div>
    </div>
  </popup-container>
</template>
